/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/react-hooks';

import debounced from '../../../../../../util/debounced';
import { GET_FILTERED_PRODUCT_GROUPS } from '../../../../../../graphql/getFilteredProductGroups';
import { GET_SUBINVENTORY_LIST_WITH_LOT_DETAILS } from '../../../../../../graphql/getSubInventoryWithLotDetails';
import { GET_PRODUCT_LIST } from '../../../../../../graphql/getProductList';
import { MAX_QUANTITY, NOTIFY_TYPE } from '../../../../../../util/constants';
import { UsePartsProps, UsePartsResult } from './AddPartsModal.types';
import { getCatalogQueryVariables, optionsWithLabel, parseCatalog, parseInventory } from './utils';

export enum INVENTORY_CHANNELS {
  CATALOG = 'Catalog',
  REPSTOCK = 'Repstock',
  CUSTOMER = 'Customer',
}

const SEARCH_DEBOUNCE_TIME = 1000;

export default function useParts({
  usageDetails,
  shouldLoadCatalogByDefault = false,
  shouldSkipCustomer = false,
  shouldSkipRepstock = false,
  userInfo,
  initialPartsCount = 0,
  openSnackBar,
}: UsePartsProps): UsePartsResult {
  const { accountNumber, salesRepId = null } = usageDetails;
  const [searchText, setSearchText] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<any[]>([]);
  const [inventoryCount, setInventoryCount] = useState<number>(initialPartsCount);
  const [inventoryPartsToAdd, setInventoryPartsToAdd] = useState<any[]>([]);
  const [catalogPartsToAdd, setCatalogPartsToAdd] = useState<any[]>([]);

  const inventoryVariables = {
    ...(!searchText && {
      limit: 50,
      offset: 0,
    }),
    subInventoryCode: accountNumber,
    salesRepId,
  };

  const { data: productGroups, loading: loadingProductGroups } = useQuery(
    GET_FILTERED_PRODUCT_GROUPS,
    {
      variables: {
        caseId: usageDetails?.caseId || '',
        caseExternalId: usageDetails?.caseExternalId || '',
        salesRepId: usageDetails?.salesRepId || '',
      },
    }
  );
  const productName = useMemo(() => selectedGroup.map(group => group.value), [selectedGroup]);

  const { data: repstockInventory, loading: loadingRepstockInventory } = useQuery(
    GET_SUBINVENTORY_LIST_WITH_LOT_DETAILS,
    {
      variables: {
        ...inventoryVariables,
        tabName: INVENTORY_CHANNELS.REPSTOCK,
        filters: { searchText, productName },
      },
      fetchPolicy: 'no-cache',
      skip: shouldSkipRepstock,
    }
  );
  const { data: customerInventory, loading: loadingCustomerInventory } = useQuery(
    GET_SUBINVENTORY_LIST_WITH_LOT_DETAILS,
    {
      variables: {
        ...inventoryVariables,
        tabName: INVENTORY_CHANNELS.CUSTOMER,
        filters: { searchText, productName },
      },
      fetchPolicy: 'no-cache',
      skip: shouldSkipCustomer,
    }
  );
  const [getCatalog, { data: catalogInventory, loading: loadingCatalogInventory }] = useLazyQuery(
    GET_PRODUCT_LIST
  );
  useEffect(() => {
    if (shouldLoadCatalogByDefault)
      getCatalog({
        variables: getCatalogQueryVariables(usageDetails, userInfo, '', productName),
      });
  }, [shouldLoadCatalogByDefault, getCatalog, usageDetails, userInfo, productName]);
  useEffect(() => {
    if (searchText.length > 2) {
      getCatalog({
        variables: getCatalogQueryVariables(usageDetails, userInfo, searchText, productName),
      });
    }
  }, [searchText, getCatalog, usageDetails, userInfo, productName]);

  const parsedRepstockInventory = useMemo(() => {
    return parseInventory(
      repstockInventory?.getSubInventoryWithLotDetails,
      usageDetails,
      inventoryPartsToAdd
    );
  }, [repstockInventory, usageDetails, inventoryPartsToAdd]);
  const parsedCustomerInventory = useMemo(() => {
    return parseInventory(
      customerInventory?.getSubInventoryWithLotDetails,
      usageDetails,
      inventoryPartsToAdd
    );
  }, [customerInventory, usageDetails, inventoryPartsToAdd]);
  const parsedCatalogInventory = useMemo(() => {
    return parseCatalog(catalogInventory?.getProductList, usageDetails, catalogPartsToAdd);
  }, [catalogInventory, usageDetails, catalogPartsToAdd]);

  const notifyItemOnHold = () =>
    openSnackBar(
      NOTIFY_TYPE.ERROR,
      'This Product/Lot/Serial is currently on hold. Remove this Product/Lot/Serial to advance this order.'
    );

  const handleInventoryCount = (
    data: { value: string; number: number },
    subInventory: any,
    completeInventory: any,
    inventoryName: INVENTORY_CHANNELS.REPSTOCK | INVENTORY_CHANNELS.CUSTOMER
  ) => {
    if (subInventory?.productOnHold) notifyItemOnHold();

    setInventoryPartsToAdd((prevParts: any) => {
      const updatedInventory = [...completeInventory];
      let cartCounter = inventoryCount;
      let decrementFlag = false;
      const newParts = [...prevParts];

      updatedInventory.forEach((inventory: any) => {
        inventory?.productDescription.forEach((item: any) => {
          item?.value.forEach((element: any) => {
            if (element.name === subInventory.productDescription) {
              element.value.forEach((item: any, index: any) => {
                if (item.lotNumber === subInventory.lotNumber) {
                  decrementFlag = data.number < item.counterValue;
                  item.changed = true;
                  item.counterValue =
                    data.number < subInventory.maxPermissibleValue
                      ? data.number
                      : subInventory.maxPermissibleValue;
                  cartCounter += item.changed ? 1 : 0;
                  item.sourceLocation = inventoryName;
                  const subInvIndex = inventoryPartsToAdd.findIndex(
                    (e: any) => e.lotNumber === subInventory.lotNumber
                  );
                  if (subInvIndex > -1) {
                    newParts[subInvIndex] = { ...element.value[index] };
                  } else {
                    newParts.push({ ...element.value[index] });
                  }
                }
              });
            }
          });
        });
      });

      if (cartCounter < inventoryCount)
        cartCounter = cartCounter === 1 && decrementFlag ? inventoryCount + 1 : cartCounter;
      else if (decrementFlag) cartCounter -= 2;

      setInventoryCount(cartCounter);
      return newParts;
    });
  };

  const handleCatalogCount = (
    data: { value: string; number: number },
    catalogInventory: any,
    completeCatalogInventory: any,
    inventoryName: INVENTORY_CHANNELS.CATALOG | null,
  ) => {
    if (catalogInventory?.productOnHold) notifyItemOnHold();

    setCatalogPartsToAdd((prevParts: any) => {
      const updatedCatalog = [...completeCatalogInventory];
      let cartCounter = inventoryCount;
      let decrementFlag = false;
      const newParts = [...prevParts];

      updatedCatalog.forEach((element: any, index: number) => {
        if (catalogInventory.productId === element.productId) {
          decrementFlag = data.number < element.counterValue;
          element.counterValue = data.number < MAX_QUANTITY ? data.number : MAX_QUANTITY;
          element.changed = true;
          element.sourceLocation = inventoryName;
          cartCounter += element.changed ? 1 : 0;
          const addedIndex = catalogPartsToAdd.findIndex(
            (e: any) => e.productId === element.productId
          );
          if (addedIndex > -1) {
            newParts[addedIndex] = { ...updatedCatalog[index] };
          } else {
            newParts.push({ ...updatedCatalog[index] });
          }
        }
      });

      if (cartCounter < inventoryCount)
        cartCounter = cartCounter === 1 && decrementFlag ? inventoryCount + 1 : cartCounter;
      else if (decrementFlag) cartCounter -= 2;

      setInventoryCount(cartCounter);
      return newParts;
    });
  };

  const searchInventory = (_e: any, { value }: any) => setSearchText(value.length > 2 ? value : '');

  return {
    searchText,
    inventoryCount,
    searchInventory: debounced(SEARCH_DEBOUNCE_TIME, searchInventory),
    repstockInventory: parsedRepstockInventory,
    loadingRepstockInventory,
    customerInventory: parsedCustomerInventory,
    loadingCustomerInventory,
    catalogInventory: parsedCatalogInventory,
    loadingCatalogInventory,
    handleInventoryCount,
    handleCatalogCount,
    inventoryPartsToAdd,
    catalogPartsToAdd,
    productGroups: optionsWithLabel(productGroups?.getProductGroups),
    loadingProductGroups,
    selectedGroup,
    setSelectedGroup,
  };
}
